<template>
  <main class="company-update">
    <div class="company-update__header">
      <h1 class="company-update__title">Company Updates</h1>
      <p class="company-update__subtitle">Latest news and announcements</p>
    </div>

    <div v-if="!loading && companyUpdate.length !== 0" class="company-update__content">
      <div
        v-for="(update, index) in companyUpdate"
        :key="index"
        class="company-update__card"
      >
        <div class="company-update__card-avatar">
          <img
            v-if="token.image"
            :src="token.image"
            :alt="token.name"
          />
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 7H7V9H9V7Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M9 11H7V13H9V11Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M9 15H7V17H9V15Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M17 7H11V9H17V7Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M17 11H11V13H17V11Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M17 15H11V17H17V15Z" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </div>
        <div class="company-update__card-content">
          <h3 class="company-update__card-title">{{ update.title }}</h3>
          <p v-html="truncate(update.body, 200)" class="company-update__card-body"></p>
          <div class="company-update__card-footer">
            <span class="company-update__card-time">
              <span v-if="calculateDay(update.updatedAt) >= 1">
                {{ calculateDay(update.updatedAt) }} days ago
              </span>
              <span v-else-if="calculateHour(update.updatedAt) >= 1">
                {{ calculateHour(update.updatedAt) }} hr ago
              </span>
              <span v-else>
                {{ calculateMin(update.updatedAt) }} min ago
              </span>
            </span>
            <a
              class="company-update__card-link"
              :href="update.link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M7 17L17 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M7 7H17V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="companyUpdate.length === 0 && !loading" class="company-update__empty">
      <div class="company-update__empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 2V8H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 13H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M16 17H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M10 9H9H8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="company-update__empty-title">No updates yet</h2>
      <p class="company-update__empty-desc">There are no updates from this company</p>
    </div>

    <div v-if="loading" class="company-update__loading">
      <SmallLoader />
    </div>
  </main>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import SmallLoader from "@/components/notification/SmallLoader.vue";
import {
  truncate,
  calculateDay,
  calculateHour,
  calculateMin,
} from "@/utils/helpers";

export default {
  name: "CompanyUpdate",

  components: {
    SmallLoader,
  },

  props: {
    currentEsop: { type: Object, default: () => {} },
  },

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
    ...mapState({
      token: (state) => state.TokenModule.tokenInfo,
      companyUpdate: (state) => state.TokenModule.tokenUpdates,
    }),
  },

  created() {
    this.getCompanyUpdates();
  },

  methods: {
    ...mapActions("TokenModule", ["getCompanyUpdate"]),

    truncate,
    calculateDay,
    calculateHour,
    calculateMin,

    async getCompanyUpdates() {
      this.loading = true;
      const payload = {
        id: this.currentEsop?.organisation._id ?? this.organisationDetails._id,
      };
      await this.getCompanyUpdate(payload);
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.company-update {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-height: 100%;

  &__header {
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__content {
    padding: 24px;
  }

  &__card {
    display: flex;
    gap: 16px;
    padding: 20px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    margin-bottom: 16px;
    transition: all 0.2s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      border-color: #4D84FF;
    }
  }

  &__card-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    color: #64748B;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__card-content {
    flex: 1;
    min-width: 0;
  }

  &__card-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__card-body {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0 0 12px 0;
    line-height: 1.5;
  }

  &__card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__card-time {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #94A3B8;
  }

  &__card-link {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #4D84FF;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #3D6FE6;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }

  &__empty-icon {
    width: 80px;
    height: 80px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 24px;
  }

  &__empty-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__empty-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__loading {
    position: relative;
    height: 320px;
  }
}
</style>
